import type { TimelineDate, TimelineItem } from "../types/timeline";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export interface TimelineBounds {
  startDate: Date;
  endDate: Date;
}

const normalizeTimestamp = (timestamp: number): number => {
  // Unix timestamps are commonly stored as seconds; JavaScript Date expects milliseconds.
  return Math.abs(timestamp) < 1_000_000_000_000 ? timestamp * 1000 : timestamp;
};

export const toDate = (value: TimelineDate): Date => {
  const date = typeof value === "number" ? new Date(normalizeTimestamp(value)) : new Date(value);

  if (Number.isNaN(date.getTime())) {
    throw new Error(`Invalid timeline date: ${String(value)}`);
  }

  return date;
};

// Fractional by default so pixel widths can stay proportional during rendering.
export const getDurationInDays = (
  startDate: TimelineDate,
  endDate: TimelineDate,
  options: { inclusive?: boolean } = {},
): number => {
  const start = toDate(startDate).getTime();
  const end = toDate(endDate).getTime();
  const duration = (end - start) / MS_PER_DAY;

  if (duration < 0) {
    throw new Error("End date must be equal to or later than start date.");
  }

  return options.inclusive ? Math.floor(duration) + 1 : duration;
};

export const getRelativeDurationPercentage = (
  child: TimelineItem,
  parent: TimelineItem,
): number => {
  const childDuration = getDurationInDays(child.startDate, child.endDate);
  const parentDuration = getDurationInDays(parent.startDate, parent.endDate);

  if (parentDuration === 0) {
    return childDuration === 0 ? 100 : 0;
  }

  return (childDuration / parentDuration) * 100;
};

export const getRelativeOffsetPercentage = (
  child: TimelineItem,
  parent: TimelineItem,
): number => {
  const parentDuration = getDurationInDays(parent.startDate, parent.endDate);
  const offsetDuration = getDurationInDays(parent.startDate, child.startDate);

  if (parentDuration === 0) {
    return 0;
  }

  return (offsetDuration / parentDuration) * 100;
};

/**
 * Computes timeline bounds across a collection of items.
 *
 * Items with missing or unparseable start/end dates are silently skipped so a
 * single malformed entry in a legacy/anonymous-merged goal store cannot crash
 * the whole React tree. Returns null when the collection is empty OR every
 * item is unusable.
 */
export const getTimelineBounds = <T extends TimelineItem>(
  items: readonly T[],
): TimelineBounds | null => {
  if (items.length === 0) {
    return null;
  }

  const parsed: Array<{ startDate: Date; endDate: Date }> = [];
  for (const item of items) {
    try {
      parsed.push({
        startDate: toDate(item.startDate),
        endDate: toDate(item.endDate),
      });
    } catch {
      // Skip items with invalid dates — the caller's null-check handles the
      // "no usable items" outcome.
    }
  }

  if (parsed.length === 0) return null;

  return parsed.reduce<TimelineBounds>(
    (bounds, entry) => ({
      startDate: entry.startDate < bounds.startDate ? entry.startDate : bounds.startDate,
      endDate: entry.endDate > bounds.endDate ? entry.endDate : bounds.endDate,
    }),
    parsed[0],
  );
};

export const clampPercentage = (percentage: number): number =>
  Math.min(100, Math.max(0, percentage));
